import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import MetaTags from "react-meta-tags";
import jwt_decode from 'jwt-decode';
import {
    Row, Col, Card, CardBody, CardHeader,
    Label, Input, Button,
} from "reactstrap";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from '../api/axios';
import { isLoading } from '../../store/actions';
import { SlotAllocationForm } from './Components/ModalForm/SlotAllocation';
import { MacrosModal } from './Components/ModalForm/MacrosTable';
import { saveAs } from "file-saver";
import Swal from 'sweetalert2';
import './parking.scss';

const FilterSection = ({
    isFilterOpen,
    setIsFilterOpen,
    selectedProperty,
    properties,
    handlePropertyChange,
    filters,
    filterData,
    handleFilterChange,
    searchQuery,
    setSearchQuery,
    activeLayout,
    toggleMacros,
    downloadExcel,
}) => (
    <div className="parking-filter-panel">
        {/* Always-visible header row */}
        <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-2">
                <button
                    className={`btn-parking btn-filter-toggle ${isFilterOpen ? 'active' : ''}`}
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                    <i className={`mdi mdi-filter-${isFilterOpen ? 'off' : 'outline'}`} />
                    {isFilterOpen ? 'Collapse Filters' : 'Expand Filters'}
                </button>
                {activeLayout && (
                    <>
                        <button className="btn-parking btn-macros" onClick={toggleMacros}>
                            <i className="mdi mdi-chart-box-outline" /> Macros
                        </button>
                        <button className="btn-parking btn-excel" onClick={downloadExcel}>
                            <i className="mdi mdi-microsoft-excel" /> Export
                        </button>
                    </>
                )}
            </div>
            {selectedProperty?.title && (
                <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                    Property: <span className="text-white">{selectedProperty.title}</span>
                </span>
            )}
        </div>

        {/* Collapsible filter row */}
        {isFilterOpen && (
            <div className="filter-card card">
                <div className="card-body">
                    <div className="filter-grid">
                        {/* Property */}
                        <div className="filter-item">
                            <label>Property</label>
                            <Input
                                type="select"
                                value={selectedProperty?._id}
                                onChange={(e) => handlePropertyChange(e.target.value)}
                            >
                                <option value="">Select a Property</option>
                                {properties?.length > 0
                                    ? properties.map((p) => (
                                        <option key={p._id} value={p._id}>{p.title}</option>
                                    ))
                                    : <option disabled>No Properties Available</option>
                                }
                            </Input>
                        </div>

                        {/* Building (conditional) */}
                        {filters.buildings.length > 0 && (
                            <div className="filter-item">
                                <label>Building</label>
                                <Input type="select" name="building" value={filterData.building} onChange={handleFilterChange}>
                                    <option value="">All Buildings</option>
                                    {filters.buildings.map((b) => <option key={b} value={b}>{b}</option>)}
                                </Input>
                            </div>
                        )}

                        {/* Parking Type */}
                        <div className="filter-item">
                            <label>Parking Type</label>
                            <Input type="select" name="parking_type" value={filterData.parking_type} onChange={handleFilterChange}>
                                <option value="">All Types</option>
                                {filters.parking_types.map((t) => <option key={t} value={t}>{t}</option>)}
                            </Input>
                        </div>

                        {/* Floor */}
                        <div className="filter-item">
                            <label>Floor</label>
                            <Input type="select" name="floor" value={filterData.floor} onChange={handleFilterChange}>
                                <option value="">All Floors</option>
                                {filters.floors.map((f) => <option key={f} value={f}>{f}</option>)}
                            </Input>
                        </div>

                        {/* Search */}
                        <div className="filter-item" style={{ maxWidth: 260 }}>
                            <label>Search</label>
                            <Input
                                type="text"
                                placeholder="Slot #, Suite #, Plate #…"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        )}
    </div>
);

const ParkingTable = ({ config = { slots: [] }, searchQuery, hoveredRow, setHoveredRow, setModalState }) => {
    const { slots = [] } = config;

    const filteredSlots = searchQuery.trim()
        ? slots.filter((slot) => {
            const q = searchQuery.toLowerCase();
            return (
                slot?.slot_no?.toString().toLowerCase().includes(q) ||
                slot?.suite_no?.toString().toLowerCase().includes(q) ||
                slot?.vehicle_no?.toString().toLowerCase().includes(q) ||
                slot?.notes?.toString().toLowerCase().includes(q)
            );
        })
        : slots;

    if (slots.length <= 0) {
        return (
            <div className="parking-table-wrapper">
                <table className="table parking-table mb-0">
                    <tbody>
                        <tr>
                            <td colSpan="6" className="text-center py-4 text-muted">
                                <i className="mdi mdi-parking fs-2 d-block mb-2" />
                                No parking data available — select a property layout to get started.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }

    const renderTooltip = (slot) => (
        <Tooltip id={`tooltip-${slot?.slot_no}`} style={{ zIndex: 9999 }}>
            {slot?.status === 'Occupied'
                ? `Slot ${slot.slot_no} · Occupied · Click to edit`
                : `Slot ${slot.slot_no} · Vacant · Click to assign`}
        </Tooltip>
    );

    return (
        <div className="parking-table-wrapper">
            {searchQuery.trim() && (
                <div className="search-meta">
                    Showing {filteredSlots.length} of {slots.length} slots
                    {filteredSlots.length === 0 && (
                        <span className="ms-2 text-danger">— No matches found</span>
                    )}
                </div>
            )}
            <table className="table parking-table table-bordered mb-0">
                <thead>
                    <tr>
                        <th>Parking #</th>
                        <th>Suite #</th>
                        <th>Plate #(s)</th>
                        <th>Notes</th>
                        <th>Type</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredSlots.length > 0 ? (
                        filteredSlots.map((slot, index) => (
                            <OverlayTrigger
                                key={index}
                                placement="left"
                                overlay={renderTooltip(slot)}
                            >
                                <tr
                                    className={`slot-row ${hoveredRow === index ? 'hovered-item' : ''}`}
                                    onClick={() => setModalState({ modal: true, slot_no: slot?.slot_no })}
                                    onMouseEnter={() => setHoveredRow(index)}
                                    onMouseLeave={() => setHoveredRow(null)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td className={`item-1 ${hoveredRow === index ? 'hovered-item' : ''} ${slot?.status === 'Occupied' ? 'slot-occupied' : 'slot-empty'}`}>
                                        {slot?.slot_no}
                                    </td>
                                    <td className={`item-2 ${hoveredRow === index ? 'hovered-item' : ''} ${slot?.status === 'Occupied' ? 'slot-occupied' : 'slot-empty'}`}>
                                        {slot?.suite_no || 'N/A'}
                                    </td>
                                    <td className={`item-4 ${hoveredRow === index ? 'hovered-item' : ''} ${slot?.status === 'Occupied' ? 'slot-occupied' : 'slot-empty'}`}>
                                        {slot?.vehicle_no || 'N/A'}
                                    </td>
                                    <td className={`item-3 ${hoveredRow === index ? 'hovered-item' : ''} ${slot?.status === 'Occupied' ? 'slot-occupied' : 'slot-empty'}`}>
                                        {slot?.notes || 'N/A'}
                                    </td>
                                    <td className={`item-5 ${hoveredRow === index ? 'hovered-item' : ''} ${slot?.status === 'Occupied' ? 'slot-occupied' : 'slot-empty'}`}>
                                        {slot.type || 'N/A'}
                                    </td>
                                    <td className={`item-6 ${hoveredRow === index ? 'hovered-item' : ''} ${slot?.status === 'Occupied' ? 'slot-occupied' : 'slot-empty'}`}>
                                        {slot.status || 'N/A'}
                                    </td>
                                </tr>
                            </OverlayTrigger>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="6" className="text-center">
                                No slots match your search.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

const AssignParking = () => {
    const [selectedProperty, setSelectedProperty] = useState({ _id: "", title: "" });
    const [filterData, setFilterData] = useState({ building: '', parking_type: "", floor: "" });
    const [filters, setFilters] = useState({ buildings: [], parking_types: [], floors: [] });
    const [activeLayout, setActiveLayout] = useState();
    const [properties, setProperties] = useState([]);
    const [isFilterOpen, setIsFilterOpen] = useState(true);
    const [modalState, setModalState] = useState({ modal: false, slot_no: '' });
    const [macrosState, setMacrosState] = useState({ modal: false, macros: null });
    const [hoveredRow, setHoveredRow] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    const dispatch = useDispatch();

    const getUserFromToken = () => {
        const token = window.localStorage.getItem('accessToken');
        if (!token) return null;
        try { return jwt_decode(token); } catch { return null; }
    };

    const fetchLayouts = async () => {
        dispatch(isLoading(true));
        try {
            const user = getUserFromToken();
            if (!user) return;
            const { building, parking_type, floor } = filterData;
            const params = {
                building_name: building, parking_type, floor,
                role: user?.role, property: selectedProperty?.title,
                company_domain: user?.domain ? user?.domain : user?.managerOf,
                manager_id: user?.id,
            };
            const response = await axios.get('/parking_new/parking-layout/layouts', { params });
            setProperties(response?.data?.property_titles || []);
            setFilters({
                buildings: response?.data?.filters?.building_names || [],
                parking_types: response?.data?.filters?.parking_types || [],
                floors: response?.data?.filters?.floors || [],
            });
            setActiveLayout(response?.data?.tableData);
            setMacrosState({ modal: false, macros: response?.data?.macros });
        } catch (error) {
            console.log(error);
        } finally {
            dispatch(isLoading(false));
        }
    };

    const downloadExcel = async () => {
        try {
            const result = await Swal.fire({
                title: 'Download Excel?',
                text: 'Export current parking slot data.',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, download!',
                cancelButtonText: 'Cancel',
            });
            if (result.isConfirmed) {
                const response = await axios.post(
                    '/parking_new/parking-layout/generate-excel',
                    {
                        property_id: selectedProperty?._id,
                        building_name: filterData?.building || null,
                        parking_type: filterData.parking_type,
                        floor_no: filterData.floor,
                    },
                    { responseType: 'blob' }
                );
                const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                saveAs(file, `ParkingSlots-${selectedProperty?.title}.xlsx`);
                Swal.fire('Downloaded!', 'Excel file saved.', 'success');
            }
        } catch (error) {
            Swal.fire('Error', 'Could not download file.', 'error');
        }
    };

    useEffect(() => { fetchLayouts(); }, [filterData, selectedProperty, modalState.modal]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilterData((prev) => {
            const updated = { ...prev, [name]: value };
            if (name === "building") { updated.parking_type = ""; updated.floor = ""; }
            else if (name === "parking_type") { updated.floor = ""; }
            return updated;
        });
    };

    const handlePropertyChange = (propertyId) => {
        const property = properties.find((p) => p._id === propertyId);
        if (property) {
            setSelectedProperty(property);
            setFilterData({ building: '', parking_type: "", floor: "" });
        }
    };

    const closeModal = () => setModalState((prev) => ({ ...prev, modal: false }));
    const toggleMacros = () => setMacrosState((prev) => ({ ...prev, modal: !macrosState.modal }));

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTags>
                    <title>{`Assign Parking | ${selectedProperty.title || "Select Property"}`}</title>
                </MetaTags>
                <div className="container-fluid">
                    <Breadcrumbs title="Home" breadcrumbItem="Assign Parking" />
                    <Row>
                        <Col xl={12}>
                            <Card>
                                <CardHeader>
                                    <FilterSection
                                        isFilterOpen={isFilterOpen}
                                        setIsFilterOpen={setIsFilterOpen}
                                        selectedProperty={selectedProperty}
                                        properties={properties}
                                        handlePropertyChange={handlePropertyChange}
                                        filters={filters}
                                        filterData={filterData}
                                        handleFilterChange={handleFilterChange}
                                        searchQuery={searchQuery}
                                        setSearchQuery={setSearchQuery}
                                        activeLayout={activeLayout}
                                        toggleMacros={toggleMacros}
                                        downloadExcel={downloadExcel}
                                    />
                                </CardHeader>
                                <CardBody>
                                    <ParkingTable
                                        config={activeLayout}
                                        searchQuery={searchQuery}
                                        hoveredRow={hoveredRow}
                                        setHoveredRow={setHoveredRow}
                                        setModalState={setModalState}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
            <SlotAllocationForm
                isOpen={modalState?.modal}
                toggle={closeModal}
                property={selectedProperty?._id}
                layout_id={activeLayout?.activeLayout?._id}
                slot_no={modalState?.slot_no}
            />
            <MacrosModal
                isOpen={macrosState?.modal}
                macros={macrosState?.macros}
                toggle={toggleMacros}
            />
        </React.Fragment>
    );
};

export { AssignParking };